import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Calculate if user has full access (FIXED VERSION)
  const hasFullAccess = () => {
    if (!userProfile) return false;
    
    // If premium subscriber, always have access
    if (userProfile.isPremium) return true;
    
    // Check if within 30-day free trial - simple date comparison
    if (userProfile.freeTrialEndsAt) {
      const trialEnd = new Date(userProfile.freeTrialEndsAt);
      const now = new Date();
      
      // Debug logging
      console.log('🔍 Access Check:', {
        trialEnd: trialEnd.toISOString(),
        now: now.toISOString(),
        hasAccess: now < trialEnd,
        hoursRemaining: ((trialEnd - now) / (1000 * 60 * 60)).toFixed(2)
      });
      
      return now < trialEnd;
    }
    
    return false;
  };

  // Calculate days remaining in free trial (FIXED VERSION)
  const getDaysRemaining = () => {
    if (!userProfile?.freeTrialEndsAt) return 0;
    
    const trialEnd = new Date(userProfile.freeTrialEndsAt);
    const now = new Date();
    const diffMs = trialEnd - now;
    
    // If trial has ended (negative difference)
    if (diffMs < 0) return 0;
    
    // Convert to days and round up
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    return days;
  };

  // Check if free trial has expired (FIXED VERSION)
  const isFreeTrialExpired = () => {
    if (!userProfile?.freeTrialEndsAt) return false;
    if (userProfile.isPremium) return false;
    
    const trialEnd = new Date(userProfile.freeTrialEndsAt);
    const now = new Date();
    
    // Expired if current time is past or equal to trial end time
    return now >= trialEnd;
  };

  // Sign up new user
  const signup = async (email, password, displayName = '') => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      const now = new Date();
      const trialEnd = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days from now
      
      // Create user profile in Firestore with free trial
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: email,
        displayName: displayName || email.split('@')[0], // Use email prefix if no displayName
        isPremium: false,
        createdAt: now.toISOString(),
        freeTrialEndsAt: trialEnd.toISOString()
      });
      
      console.log('✅ User created with 30-day free trial:', {
        uid: userCredential.user.uid,
        trialEnds: trialEnd.toISOString()
      });
      
      return userCredential;
    } catch (error) {
      console.error('❌ Signup error:', error);
      throw error;
    }
  };

  // Login user
  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  // Logout user
  const logout = () => {
    return signOut(auth);
  };

  // Reset password
  const resetPassword = (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  // Fetch user profile from Firestore
  const fetchUserProfile = async (uid) => {
    try {
      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('📋 User profile fetched:', {
          email: data.email,
          isPremium: data.isPremium,
          freeTrialEndsAt: data.freeTrialEndsAt,
          createdAt: data.createdAt
        });
        setUserProfile(data);
        return data;
      } else {
        console.warn('⚠️  No user profile found in Firestore for:', uid);
      }
    } catch (error) {
      console.error('❌ Error fetching user profile:', error);
    }
  };

  // Update user premium status
  const updatePremiumStatus = async (uid, isPremium) => {
    try {
      await setDoc(doc(db, 'users', uid), {
        isPremium: isPremium,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      
      setUserProfile(prev => ({ ...prev, isPremium }));
    } catch (error) {
      console.error('Error updating premium status:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔐 Auth state changed:', user ? user.email : 'No user');
      setCurrentUser(user);
      
      if (user) {
        await fetchUserProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    logout,
    resetPassword,
    updatePremiumStatus,
    isPremium: userProfile?.isPremium || false,
    hasFullAccess: hasFullAccess(),
    freeTrialEndsAt: userProfile?.freeTrialEndsAt,
    daysRemaining: getDaysRemaining(),
    isFreeTrialExpired: isFreeTrialExpired()
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};